import clientAPI from "@/api/ClientAPIMixin.js";
import QEvent from '@/views/planning/QEvent.js';
import {
  DateHelper,
  DomHelper,
  DragHelper,
} from "@bryntum/schedulerpro";


export default {
  name: 'SchedulerDragMixin',
  mixins: [clientAPI],

  data() {
    return {
      dragHelper: null,
    };
  },

  mounted() { },

  methods: {
    async initializeDrag() {
      if (!this.dragHelper) {
        //console.log('initializing drag');
        const callCreateProxy = (obj) => this.createProxy(obj);
        const callOnTaskDragStart = (obj) => this.onTaskDragStart(obj);
        const callOnTaskDrag = (obj) => this.onTaskDrag(obj);
        const callOnTaskDrop = async (obj) => this.onTaskDrop(obj);
        const sm = this.scheduler.scrollManager;
        this.dragHelper = new DragHelper(
          {
            cloneTarget: true,
            autoSizeClonedTarget: false,
            dropTargetSelector: '.b-timeline-subgrid', // Only allow drops on the schedule area
            targetSelector: '.br-draggable',
            dropTargetCls: 'test123',
            invalidCls: 'dragproxyOverInvalidTarget',
            scrollManager: sm,
            createProxy: callCreateProxy,
            listeners: {
              dragstart: callOnTaskDragStart,
              drag: callOnTaskDrag,
              drop: callOnTaskDrop,
              thisObj: this
            },
          },
        );
      }
    },

    createProxy(element) {
      const oneHourInMs = 1000 * 60 * 60;
      const numHours = (this.isDayMode ? (element.dataset.estimatedtime ?? 2) : 24);
      var task = {
        id: element.dataset.jobid,
        name: element.dataset.subject,
        durationMS: oneHourInMs * numHours,
      };

      const
        proxy = document.createElement('div'),
        durationInPx = this.scheduler.timeAxisViewModel.getDistanceForDuration(task.durationMS);

      // Fake an event bar
      proxy.classList.add('b-sch-event', 'dragproxy', `b-sch-${this.scheduler.mode}`);
      proxy.innerHTML = `<div>${task.name}</div>`;
      const height = `${this.scheduler.rowHeight - (2 * this.scheduler.resourceMargin)}px`;
      proxy.style.height = height;
      proxy.style.maxHeight = height;
      proxy.style.minHeight = height;
      const width = `${durationInPx}px`;
      proxy.style.width = width;
      proxy.style.minWidth = width;
      proxy.style.maxWidth = width;

      return proxy;
    },

    onTaskDragStart({ context }) {
      var element = context.grabbed;
      var task = new QEvent();
      task.name = element.dataset.subject;
      task.jobId = element.dataset.jobid;
      task.durationNum = element.dataset.estimatedtime;
      task.duration = element.dataset.estimatedtime + 'h';
      task.durationUnit = 'hour';
      context.task = task;   // save a reference to the task so we can access it later
    },

    onTaskDrag({ context }) {
      const
        task = context.task,
        target = context.target,
        coordinate = DomHelper[`getTranslate${this.scheduler.mode === 'horizontal' ? 'X' : 'Y'}`](context.element),  // Coordinates required when used in vertical mode, since it does not use actual columns
        startDate = this.scheduler.getDateFromCoordinate(coordinate, 'round', false),
        endDate = startDate && DateHelper.add(startDate, task.duration, task.durationUnit),
        resource = target && this.scheduler.resolveResourceRecord(target);

      document.querySelectorAll(".dragHoverRow").forEach(el => { el.classList.remove('dragHoverRow') })  // Removing prevoiusly targeted outliend rows
      context.valid = Boolean(startDate && resource?.name);
      if (context.valid) {
        target?._domData?.rowElement?.classList.add('dragHoverRow'); //apply outline effect to target row

        context.resource = resource;    // Save reference to resource so we can use it in onTaskDrop
        task.startDate = startDate;
        task.endDate = endDate;
        context.coordinate = coordinate;
      }
      //var format = this.isDayMode ? 'HH:mm' : 'D. MMM - HH:mm';
    },

    getEventsForResourceOnDate(resource, dayStart, dayEnd) {
      const scheduler = this.scheduler;
      if (!scheduler) return [];

      let events = [];
      try {
        if (typeof scheduler.getEventsForResource === 'function') {
          events = scheduler.getEventsForResource(resource) || [];
        } else if (scheduler.eventStore && typeof scheduler.eventStore.getEventsForResource === 'function') {
          events = scheduler.eventStore.getEventsForResource(resource) || [];
        } else if (scheduler.eventStore?.records) {
          const recs = scheduler.eventStore.records;
          events = recs.filter(e => {
            const assigned = (typeof e.isAssignedTo === 'function') ? e.isAssignedTo(resource) : (e.resourceId === resource.id || e.resources?.some?.(r => r.id === resource.id));
            return assigned;
          });
        }
      } catch (e) {
        console.error('Error getting events for resource', resource, e);
      }

      const list = events.filter(e => e.startDate < dayEnd && e.endDate > dayStart);
      list.sort((a, b) => a.startDate - b.startDate);
      return list;
    },

    getWorkhourStartDateFor(dropDate) {
      const workhourStartDecimal = parseFloat(this.WorkhourStart ?? 0);
      const startHours = Math.floor(workhourStartDecimal);
      const startMinutes = Math.round((workhourStartDecimal - startHours) * 60);
      return new Date(dropDate.getFullYear(), dropDate.getMonth(), dropDate.getDate(), startHours, startMinutes, 0, 0);
    },

    findFirstAvailableSlotOnDay(resource, dropDate, durationHours) {
      const dayStart = new Date(dropDate.getFullYear(), dropDate.getMonth(), dropDate.getDate(), 0, 0, 0, 0);
      const dayEnd = new Date(dropDate.getFullYear(), dropDate.getMonth(), dropDate.getDate(), 23, 59, 59, 999);

      let candidate = this.getWorkhourStartDateFor(dropDate);
      const durationMs = (parseFloat(durationHours) || 1) * 60 * 60 * 1000;

      const events = this.getEventsForResourceOnDate(resource, dayStart, dayEnd);

      for (const e of events) {
        if (candidate.getTime() + durationMs <= e.startDate.getTime()) {
          return { start: new Date(candidate), end: new Date(candidate.getTime() + durationMs) };
        }
        if (e.endDate.getTime() > candidate.getTime()) {
          candidate = new Date(e.endDate);
        }
      }

      return { start: new Date(candidate), end: new Date(candidate.getTime() + durationMs) };
    },

    async onTaskDrop({ context }) {
      const task = context.task;
      const resource = context.resource;
      // If drop was done in a valid location create the appointment
      if (context.valid) {
        if (!this.isDayMode) { //if not day mode show dialog
          // Default time when dropping in week/month: start at WorkhourStart, duration = job estimate
          try {
            const dropDate = task.startDate ? new Date(task.startDate) : new Date();
            const durationHours = parseFloat(task.durationNum ?? 1) || 1;

            const { start, end } = this.findFirstAvailableSlotOnDay(resource, dropDate, durationHours);

            task.startDate = start;
            task.endDate = end;
          } catch (e) {
            // Fallback: keep scheduler-provided times if any error occurs
          }
          this.openEventDialog({ eventRecord: task, resourceRecord: resource });
        }
        else {
          const dto = {
            Id: null,
            Subject: task.name,
            AppointmentDate: task.startDate,
            StartTime: task.startDate,
            EndTime: task.endDate,
            JobId: task.jobId,
            Participants: [{ Id: resource.id, Name: resource.name }]
          };
          await clientAPI.methods.createAppointment(dto)
            .then((response) => {
              this.addOrUpdateEventInStoreById(response.Id);
              this.$store.dispatch("plannerstore/RemoveUnplannedJobById", response.JobId);
            });
        }
      }
    },
  },

  computed: {
    plannerScope() {
      return this.$store.getters["prefrences/Prefrence"]("PlannerScope");
    },

    isDayMode() {
      return this.plannerScope == 'QDay';
    },
  },

  watch: {},
}